import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import React from 'react'

import {
  fetchAfterschoolCatalogue,
  fetchAfterschoolOrder,
  saveAfterschoolCatalogue,
  saveAfterschoolOrder
} from 'api/procurement'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import { errorText } from '../components/errorText'
import AUDIT from './auditCounts.json'
import SEED from './seed.json'

// Afterschool Games — centre stock check over the confirmed games list
// (57 games + Robotics 4, added 2026-09-08),
// plus an order view of whatever the check finds short.
// LOCAL PREVIEW BUILD: the list ships as a bundled seed (the sheet Aswathi
// signed off on; setCost = ₹ per full set from its priced items) and
// confirmations persist to localStorage per centre. The backend
// (CentreStockNorm/CentreStockCount + the "afterschool games" catalogue tag)
// replaces both once the flow is approved.
// Game-level reference id (user 2026-08-22): the items share a family
// prefix — CG_ECA_WTS_001..006 → the game's id is CG_ECA_WTS. Games whose
// items span families fall back to the longest shared '_'-segment prefix
// (two segments minimum), else no game id.
const gameRefOf = (items) => {
  const bases = [
    ...new Set(
      items.map((it) => (it.ref || '').replace(/_\d+$/, '')).filter(Boolean)
    )
  ]
  if (!bases.length) return ''
  if (bases.length === 1) return bases[0]
  const segs = bases.map((b) => b.split('_'))
  const first = segs[0]
  const out = []
  for (let i = 0; i < first.length; i += 1) {
    if (segs.every((sg) => sg[i] === first[i])) out.push(first[i])
    else break
  }
  return out.length >= 2 ? out.join('_') : ''
}

const buildGames = (seed) =>
  seed.map((g) => ({
    ...g,
    // A game is one set per centre; components live under the expander.
    required: 1,
    components: g.items.length,
    // What the set adds up to in pieces (user 2026-08-24: '1 set' next to
    // items requiring 4/4/2/2 read as a contradiction — say what a set holds).
    pieces: g.items.reduce((s, it) => s + (Number(it.qty) || 0), 0),
    gameRef: gameRefOf(g.items)
  }))

// Lookup for group headers built from row data (order view / my order).
// Keyed `${programme}|${game}` inline — gk() is declared further down and
// this block runs at module load.
const buildGameRef = (games) => {
  const m = {}
  games.forEach((g) => {
    m[`${g.programme}|${g.game}`] = g.gameRef
  })
  return m
}

// Mutable module bindings (user 2026-08-24: the catalogue lives in the
// backend now; the bundled seed is only the instant first paint). Every
// render site reads these at call time, so applyCatalogue() below swaps
// the data for the whole module at once.
let GAMES = buildGames(SEED)
let GAME_REF = buildGameRef(GAMES)
let PROGRAMMES = [...new Set(GAMES.map((g) => g.programme))]

// Count-store key. Two programmes can share a game name (ECA and Public
// Speaking both have "Guess Me") — bare g.game would merge their counts.
const gk = (g) => `${g.programme}|${g.game}`

// July audit round: per-centre on-hand counts keyed by reference id
// (auditCounts.json, from the audit sheet's cleaned column). Used only to
// PRE-FILL the qty cells — the centre reviews, corrects and saves.
// Centre names map to the audit tabs by slug ("HRBR Layout" → hrbr);
// Alfred spells Harlur "Haralur".
const auditFor = (centreName) => {
  const s = (centreName || '')
    .toLowerCase()
    .replace('layout', '')
    .replace(/[^a-z]/g, '')
  // Alfred vs audit-tab spellings: 'Haralur' → 'harlur', 'Sadashivnagar' →
  // 'sadashivanagar' (the second one surfaced via the HQ view 2026-08-22 —
  // the centre had silently gotten no pre-fill).
  const alias = { haralur: 'harlur', sadashivnagar: 'sadashivanagar' }
  return AUDIT[alias[s] || s] || {}
}

// Roll-up of a game's item counts (items are the only thing counted;
// the game row just reports them — user 2026-08-21). Items with no
// catalogue qty are left out of the denominator.
const rollup = (g, itemCounts, audit = {}) => {
  const bucket = itemCounts[gk(g)] || {}
  let denom = 0
  let counted = 0
  let ok = 0
  let latest = ''
  // Items not yet saved still show through via their July-audit pre-fill,
  // so the collapsed row reflects the audit instead of looking empty.
  let auditCovered = 0
  let auditOk = 0
  g.items.forEach((it, i) => {
    const req = Number(it.qty)
    if (!it.qty || Number.isNaN(req)) return
    denom += 1
    const saved = bucket[String(i)]
    if (saved) {
      counted += 1
      if (saved.have >= req) ok += 1
      if (saved.at > latest) latest = saved.at
      return
    }
    const a = it.ref ? audit[it.ref.toUpperCase()] : undefined
    if (a !== undefined) {
      auditCovered += 1
      if (a >= req) auditOk += 1
    }
  })
  const state = counted
    ? counted > ok
      ? 'short'
      : counted < denom
      ? 'partial'
      : 'ok'
    : auditCovered
    ? 'audit'
    : 'todo'
  return { denom, counted, ok, latest, state, auditCovered, auditOk }
}

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

// ---- PO-format export (user 2026-08-22) -------------------------------
// The order-document register standardised for print procurement (the
// At-Apartment daily-sheet format): # | Product (Ref) | Print specification |
// Paper | Sides/file | Qty | Total sides | Rate | Total. Goods rows show '—'
// in the print columns. GST/Invoice are OMITTED — the afterschool sheet has
// no GST data. ⛔ Money stays the existing unit×qty maths, never re-derived
// as sides×ppp: the source sheet mixes three pricing shapes per row.
export const PO_HEADERS = [
  '#',
  'Product',
  'Print specification',
  'Paper',
  'Sides/file',
  'Qty',
  'Total sides',
  'Rate (Rs)',
  'Total (Rs)',
  'Remarks'
]
export const PO_ALIGN = [
  'right', false, false, false, 'right', 'right', 'right', 'right', 'right', false
]
// entries: {name, ref, spec, paper, sides, qty, rate, total, link}
// withFile appends the artwork URL column (Excel only — not clickable in PDF).
export const poRows = (entries, { withFile } = {}) =>
  entries.map((e, i) => [
    i + 1,
    e.ref ? `${e.name} (${e.ref})` : e.name,
    e.spec || '—',
    e.paper || '—',
    e.sides != null ? e.sides : '—',
    Number(e.qty || 0),
    e.sides != null ? Math.round(e.sides * Number(e.qty || 0) * 100) / 100 : '—',
    e.rate != null ? Math.round(Number(e.rate) * 100) / 100 : 'no price yet',
    e.total != null ? Math.round(Number(e.total)) : '',
    e.remarks || '',
    ...(withFile ? [e.link || ''] : [])
  ])
export const poTotals = (entries) => [
  'Total',
  null,
  null,
  null,
  null,
  String(entries.reduce((s, e) => s + Number(e.qty || 0), 0)),
  null,
  null,
  String(
    Math.round(entries.reduce((s, e) => s + (Number(e.total) || 0), 0))
  ),
  null
]

// Spec/paper/sides/ppp/link for a seed item — used to decorate server-side
// order lines (which carry only the string identity) at export time.
const buildSpecMap = (seed) => {
  const m = {}
  seed.forEach((g) =>
    g.items.forEach((it) => {
      m[`${g.game}|${it.name}`] = it
    })
  )
  return m
}
let SPEC_MAP = buildSpecMap(SEED)
export const seedSpecFor = (game, itemName) =>
  SPEC_MAP[`${game}|${itemName}`] || {}

// ---- backend catalogue (user 2026-08-24) ------------------------------
// The games master lives in Alfred now (AfterschoolGame/AfterschoolItem);
// HQ edits pricing in place and the source sheet is retired. The bundled
// seed stays as the instant first paint and the offline fallback; the
// fetch swaps it out module-wide and version-bumps subscribed components.
let CAT_FROM_SERVER = false
let catVersion = 0
const catListeners = new Set()

const mapApiGame = (g) => ({
  programme: g.programme,
  game: g.game,
  uuid: g.uuid,
  setCost: g.setCost != null ? Number(g.setCost) : undefined,
  items: g.items.map((it) => {
    const o = {
      name: it.name,
      ref: it.ref || '',
      uuid: it.uuid,
      qty: it.qty != null ? String(Number(it.qty)) : ''
    }
    if (it.link) o.link = it.link
    if (it.spec) o.spec = it.spec
    if (it.paper) o.paper = it.paper
    if (it.sides != null) o.sides = Number(it.sides)
    if (it.ppp != null) o.ppp = Number(it.ppp)
    // ⛔ no cost key at all when unpriced — orderRowsFor checks 'cost' in it.
    if (it.cost != null) o.cost = Number(it.cost)
    return o
  })
})

const applyCatalogue = (seedShaped, fromServer) => {
  GAMES = buildGames(seedShaped)
  GAME_REF = buildGameRef(GAMES)
  PROGRAMMES = [...new Set(GAMES.map((g) => g.programme))]
  SPEC_MAP = buildSpecMap(seedShaped)
  CAT_FROM_SERVER = !!fromServer
  catVersion += 1
  catListeners.forEach((fn) => fn(catVersion))
}

let catFetchStarted = false
const ensureCatalogue = (force) => {
  if (catFetchStarted && !force) return
  catFetchStarted = true
  fetchAfterschoolCatalogue()
    .then((data) => {
      if (data && data.games && data.games.length) {
        applyCatalogue(data.games.map(mapApiGame), true)
      }
    })
    .catch(() => {
      // Endpoint not deployed / offline: the bundled seed keeps serving.
    })
}

const useCatalogue = () => {
  const [v, setV] = React.useState(catVersion)
  React.useEffect(() => {
    const fn = (nv) => setV(nv)
    catListeners.add(fn)
    ensureCatalogue()
    return () => catListeners.delete(fn)
  }, [])
  return v
}

// ---- costing provenance on hover (user 2026-08-24) ---------------------
// The centre team verifies a price without leaving the table: hovering a
// unit or to-order amount replays the sheet's own maths — print rows as
// sides/file × PPP on the stated paper, pack rows as pack price ÷ qty —
// never a fresh calculation (the source sheet mixes three pricing shapes
// per row, so the formula only shows when it reproduces the stored amount).
const near = (a, b) =>
  a != null && b != null && Math.abs(a - b) <= Math.max(1, Math.abs(b) * 0.01)
const rupees = (n) => inr(Math.round(Number(n) * 100) / 100)
const unitMathLines = (it, unit) => {
  if (unit == null) return []
  const lines = []
  if (it.spec) lines.push(it.spec)
  if (it.paper) lines.push(`Paper: ${it.paper}`)
  const sides = Number(it.sides)
  const ppp = it.ppp != null ? Number(it.ppp) : null
  const qty = Number(it.qty)
  if (sides > 1 && ppp != null && near(sides * ppp, unit)) {
    lines.push(
      `${sides} sides/file × ${rupees(ppp)}/side = ${rupees(unit)} per unit`
    )
  } else if (qty > 1 && it.cost != null) {
    lines.push(
      `${rupees(it.cost)} for ${qty} per centre → ${rupees(unit)} each`
    )
  } else {
    lines.push(`Flat unit price ${rupees(unit)}`)
  }
  return lines
}
// Game-total hover (user 2026-08-24): the group subtotal lists the per-item
// maths it is the sum of, so the game's ₹ can be checked without expanding.
const groupCostLines = (entries) => {
  const lines = entries.map((e) =>
    e.total != null
      ? `${e.name}: ${e.qty} × ${rupees(e.unit)} = ${rupees(e.total)}`
      : `${e.name}: no price yet`
  )
  return lines.length > 12
    ? [...lines.slice(0, 12), `…and ${lines.length - 12} more`]
    : lines
}
const CostHint = ({ lines, children }) =>
  lines && lines.length ? (
    <Tooltip
      arrow
      title={
        <div>
          {lines.map((t, i) => (
            <div key={i}>{t}</div>
          ))}
        </div>
      }>
      <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>
        {children}
      </span>
    </Tooltip>
  ) : (
    children
  )

// The item's print file / product page, in the SAME idiom as the main
// catalogue ('view ↗' anchor — orange via the procurement theme), so the
// catalogue, stock check and order view all read identically and centres
// never have to hop to the catalogue to see what an item is (user 2026-08-22).
const ItemLink = ({ link }) =>
  link ? (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      title="Open the file for this item"
      onClick={(e) => e.stopPropagation()}
      style={{ marginLeft: 8, fontSize: 12, whiteSpace: 'nowrap' }}>
      view ↗
    </a>
  ) : null

const storeKey = (centreId) => `procurement.afterschool.counts.${centreId || 'all'}`
const itemStoreKey = (centreId) => `${storeKey(centreId)}.items`

const loadStore = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key)) || {}
  } catch (e) {
    return {}
  }
}

const useStyles = makeStyles((theme) => ({
  subTabs: { borderBottom: '1px solid #eee', marginBottom: theme.spacing(2) },
  chipRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  fitTable: { tableLayout: 'fixed', width: '100%', background: '#fff' },
  // P2 (user 2026-08-21): header stays frozen while the game rows scroll.
  headCell: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: '#fff',
    borderBottom: '2px solid #ddd'
  },
  wrap: { wordBreak: 'break-word', whiteSpace: 'normal' },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap'
  },
  gameName: { fontWeight: 600 },
  inlineMeta: { color: theme.palette.text.secondary, fontSize: 12 },
  expandBtn: { padding: 2 },
  expandOpen: { transform: 'rotate(180deg)' },
  compRow: { background: '#fafafa' },
  // Game header rows in the grouped Order view (user 2026-08-22 P3).
  orderGroup: {
    background: '#f7f4ee',
    '& td': { fontWeight: 600, borderTop: '1px solid #e8e4dc' }
  },
  short: { color: '#c62828', fontWeight: 600 },
  ok: { color: '#2e7d32', fontWeight: 600 },
  muted: { color: theme.palette.text.secondary },
  totalRow: { '& td': { fontWeight: 700, borderTop: '2px solid #ddd' } },
  search: { minWidth: 260 }
}))

// ---------------------------------------------------------------- stock check
const StockCheck = ({ itemCounts, onSaveAll, audit }) => {
  const classes = useStyles()
  const [expanded, setExpanded] = React.useState(null) // gk(game)
  // Unsaved item-qty edits; the cells are always editable, the one Save
  // commits. Items are the only inputs — the game row is a roll-up.
  const [itemDrafts, setItemDrafts] = React.useState({}) // per `${gk}|${idx}`
  // Header filters (user 2026-08-20): search box on Game, dropdown on Category.
  const [qGame, setQGame] = React.useState('')
  const [qCat, setQCat] = React.useState('all')

  // Full list always (user 2026-08-21 — state-filter chips removed);
  // search + category in the header are the only narrowing.
  // Search reaches inside the game (user 2026-08-24: a reference id pasted
  // from the sheet must find its item): game name, game ref, item names and
  // item refs all match; an item-level hit force-opens the game below.
  const q = qGame.trim().toLowerCase()
  const gameMatches = (g) =>
    g.game.toLowerCase().includes(q) ||
    (g.gameRef || '').toLowerCase().includes(q)
  const itemMatches = (g) =>
    g.items.some(
      (it) =>
        it.name.toLowerCase().includes(q) ||
        (it.ref || '').toLowerCase().includes(q)
    )
  const shown = GAMES.filter(
    (g) =>
      (!q || gameMatches(g) || itemMatches(g)) &&
      (qCat === 'all' || g.programme === qCat)
  )

  const nEdits = Object.keys(itemDrafts).length

  // Cell pre-fill: unsaved edit > saved count > July-audit count for this
  // centre > catalogue qty.
  const cellValue = (g, it, i) => {
    const k = `${gk(g)}|${i}`
    if (k in itemDrafts) return itemDrafts[k]
    const saved = (itemCounts[gk(g)] || {})[String(i)]
    if (saved) return String(saved.have)
    const a = it.ref ? audit[it.ref.toUpperCase()] : undefined
    if (a !== undefined) return String(a)
    return it.qty || ''
  }

  // One Save for the whole list: commits every countable item of every
  // game shown with whatever its cell says — edited or pre-filled.
  const saveAll = () => {
    const entries = []
    shown.forEach((g) => {
      g.items.forEach((it, i) => {
        const value = cellValue(g, it, i)
        if (value === '' || Number.isNaN(Number(value))) return
        entries.push([gk(g), String(i), Number(value)])
      })
    })
    onSaveAll(entries)
    setItemDrafts({})
  }

  return (
    <div>
      <Paper variant="outlined">
        <Table size="small" className={classes.fitTable}>
          <TableHead>
            <TableRow>
              <TableCell width="32%" className={classes.headCell}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Game / item / ref — search"
                  value={qGame}
                  onChange={(e) => setQGame(e.target.value)}
                  fullWidth
                />
              </TableCell>
              <TableCell width="14%" className={classes.headCell}>
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  value={qCat}
                  onChange={(e) => setQCat(e.target.value)}
                  fullWidth>
                  <MenuItem value="all">Category — all</MenuItem>
                  {PROGRAMMES.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell width="11%" className={classes.headCell}>
                Required
              </TableCell>
              <TableCell width="15%" className={classes.headCell}>
                We have
              </TableCell>
              <TableCell width="28%" className={classes.headCell}>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  disabled={!shown.length}
                  onClick={saveAll}>
                  Save all {shown.length} shown
                  {nEdits ? ` · ${nEdits} edited` : ''}
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((g) => {
              const r = rollup(g, itemCounts, audit)
              const isOpen = expanded === gk(g) || Boolean(q && itemMatches(g))
              return (
                <React.Fragment key={gk(g)}>
                  {/* Whole row toggles the items — the chevron alone was
                      undiscoverable (user 2026-08-21). */}
                  <TableRow
                    hover
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpanded(isOpen ? null : gk(g))}>
                    <TableCell>
                      <span className={classes.nameCell}>
                        <span className={classes.gameName}>{g.game}</span>
                        <IconButton
                          size="small"
                          className={classes.expandBtn}
                          title="Count the items inside this game">
                          <ExpandMoreIcon
                            fontSize="small"
                            className={isOpen ? classes.expandOpen : undefined}
                          />
                        </IconButton>
                        <span className={classes.inlineMeta}>
                          {g.components} item{g.components > 1 ? 's' : ''}
                        </span>
                      </span>
                    </TableCell>
                    <TableCell className={classes.muted}>{g.programme}</TableCell>
                    <TableCell>
                      {g.required} set
                      {g.pieces > 0 && (
                        <span className={classes.inlineMeta}>
                          {' '}
                          · {g.pieces} pcs
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.state === 'todo' ? (
                        <span className={classes.muted}>not counted</span>
                      ) : r.state === 'audit' ? (
                        <span
                          className={
                            r.auditOk < r.auditCovered
                              ? classes.short
                              : classes.muted
                          }>
                          {r.auditOk}/{r.auditCovered} ok — July audit
                        </span>
                      ) : r.state === 'short' ? (
                        <span className={classes.short}>
                          {r.ok} of {r.denom} — short
                        </span>
                      ) : r.state === 'partial' ? (
                        <span>
                          {r.counted} of {r.denom} counted
                        </span>
                      ) : (
                        <span className={classes.ok}>
                          {r.ok}/{r.denom} items ✓
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.state === 'todo' ? (
                        <span className={classes.muted}>
                          click the row to count its items
                        </span>
                      ) : r.state === 'audit' ? (
                        <span className={classes.muted}>
                          from July audit — review &amp; save
                        </span>
                      ) : r.state === 'short' ? (
                        <span className={classes.short}>short {r.latest}</span>
                      ) : r.state === 'partial' ? (
                        <span className={classes.muted}>
                          partly counted {r.latest}
                        </span>
                      ) : (
                        <span className={classes.ok}>confirmed {r.latest}</span>
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen &&
                    g.items.map((it, i) => {
                      const k = `${gk(g)}|${i}`
                      const saved = (itemCounts[gk(g)] || {})[String(i)]
                      const reqQty = it.qty ? Number(it.qty) : null
                      const itValue = cellValue(g, it, i)
                      const itShort =
                        saved && reqQty !== null && saved.have < reqQty
                      return (
                        <TableRow key={k} className={classes.compRow}>
                          <TableCell
                            style={{ paddingLeft: 40 }}
                            className={classes.wrap}>
                            {it.name}
                            <ItemLink link={it.link} />
                          </TableCell>
                          <TableCell className={classes.muted}>
                            {it.ref || '—'}
                          </TableCell>
                          <TableCell className={classes.muted}>
                            {it.qty || '—'}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              variant="outlined"
                              value={itValue}
                              onChange={(e) => {
                                // Capture before setState (React 16 event pooling).
                                const v = e.target.value
                                setItemDrafts((prev) => ({ ...prev, [k]: v }))
                              }}
                              inputProps={{ min: 0, style: { width: 52 } }}
                            />
                          </TableCell>
                          <TableCell>
                            {saved ? (
                              <span
                                className={itShort ? classes.short : classes.ok}>
                                {itShort ? 'short' : 'saved'} {saved.at}
                              </span>
                            ) : (
                              <span className={classes.muted}>—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                </React.Fragment>
              )
            })}
            {!shown.length && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="textSecondary">
                    No games match the search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  )
}

// ---------------------------------------------------------------- order view
// Item-level deficits from the stock check: says exactly which item to
// reprint or buy, not just which game is short.
// Item-level deficits: saved count where one exists, else the July-audit
// pre-fill — so the order (and its total) shows before the first save.
// Shared by OrderView and the wrapper's tab label.
const orderRowsFor = (itemCounts, audit) => {
  const rows = []
  GAMES.forEach((g) => {
    const bucket = itemCounts[gk(g)] || {}
    g.items.forEach((it, i) => {
      const req = Number(it.qty)
      if (!it.qty || Number.isNaN(req)) return
      const saved = bucket[String(i)]
      const a = it.ref ? audit[it.ref.toUpperCase()] : undefined
      const have = saved ? saved.have : a
      if (have === undefined || have >= req) return
      const need = req - have
      const unit = 'cost' in it ? it.cost / req : null
      rows.push({
        ...it,
        game: g.game,
        programme: g.programme,
        req,
        have,
        need,
        unit,
        provisional: !saved,
        orderCost: unit != null ? unit * need : null
      })
    })
  })
  return rows
}

// Landing-board signal (CenterTeam Variant A cards): the same saved-or-audit
// deficit scan the tab label uses, computed straight from localStorage so the
// board needs no API call. Returns games short + the ₹ floor to order.
export const afterschoolSummary = (centreId, centreName) => {
  const counts = loadStore(itemStoreKey(centreId))
  const rows = orderRowsFor(counts, auditFor(centreName))
  return {
    shortGames: new Set(rows.map((r) => `${r.programme}|${r.game}`)).size,
    shortItems: rows.length,
    unpriced: rows.filter((r) => r.orderCost == null).length,
    audited: Object.keys(auditFor(centreName)).length > 0,
    total: Math.round(rows.reduce((s, r) => s + (r.orderCost || 0), 0))
  }
}

// Which lifecycle states lock the month for the centre (mirrors capex).
const ORDER_LOCKED = ['submitted', 'approved', 'sent', 'received']
const ORDER_STATUS_LABEL = {
  draft: 'Draft',
  submitted: 'Submitted — with HQ for review',
  approved: 'Approved by HQ',
  rejected: 'HQ asked for changes — edit and resubmit',
  sent: 'Ordered by HQ',
  received: 'Received'
}

// ---------------------------------------------------------------- my order
// The PART of the gap the centre wants bought now (user 2026-08-22: nobody
// buys the whole ₹1-1.5L in one go). Items come in from the Order view's
// 'add' buttons; qty is editable below the shortfall. Draft/submit mirror
// capex — submit stamps a PO and locks until HQ decides. No budget block.
const MyOrder = ({
  order,
  onQty,
  onRemark,
  onRemove,
  onSave,
  saving,
  centreName,
  month
}) => {
  const classes = useStyles()
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [sopOpen, setSopOpen] = React.useState(false)
  // Opener/closer per game (user 2026-08-22) — here groups default OPEN,
  // because the quantities are being edited; a closed set tracks collapses.
  const [closedGroups, setClosedGroups] = React.useState(() => new Set())
  const toggleGroup = (key) =>
    setClosedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const locked = ORDER_LOCKED.includes(order.status)
  const lines = order.lines
  const groups = []
  lines.forEach((l, idx) => {
    const key = `${l.programme}|${l.game}`
    let g = groups[groups.length - 1]
    if (!g || g.key !== key) {
      g = { key, game: l.game, programme: l.programme, rows: [], total: 0 }
      groups.push(g)
    }
    g.rows.push({ l, idx })
    g.total +=
      l.unit_price != null ? Number(l.qty || 0) * Number(l.unit_price) : 0
  })
  const total = lines.reduce(
    (s, l) => s + (l.unit_price != null ? Number(l.qty || 0) * Number(l.unit_price) : 0),
    0
  )
  const anyUnpriced = lines.some((l) => l.unit_price == null)
  const monthLabel = `${month}-01`.slice(0, 7)


  return (
    <div>
      <Box display="flex" alignItems="center" mb={1} style={{ gap: 10 }}>
        <Chip
          size="small"
          label={ORDER_STATUS_LABEL[order.status] || order.status}
          color={order.status === 'draft' ? 'default' : 'primary'}
        />
        {order.po_number ? (
          <span className={classes.inlineMeta}>PO {order.po_number}</span>
        ) : null}
        <IconButton
          size="small"
          title="How this works"
          onClick={() => setSopOpen(true)}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <span style={{ flex: 1 }} />
        {!locked && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={saving}
              onClick={() => onSave(false)}>
              Save draft
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={saving || !lines.length}
              onClick={() => setConfirmOpen(true)}>
              Submit
            </Button>
          </>
        )}
      </Box>
      {order.status === 'rejected' && order.review_remarks ? (
        <Box mb={1} p={1.5} style={{ background: '#fbe9e7', borderRadius: 8 }}>
          <Typography variant="body2">
            <b>HQ sent this back:</b> {order.review_remarks}
          </Typography>
        </Box>
      ) : null}
      {locked ? (
        <Box mb={1} p={1.5} style={{ background: '#e3f2fd', borderRadius: 8 }}>
          <Typography variant="body2">
            {ORDER_STATUS_LABEL[order.status]} — this month's order is locked.
            HQ can reopen it if something needs to change.
          </Typography>
        </Box>
      ) : null}
      {!lines.length ? (
        <Typography variant="body2" color="textSecondary">
          Nothing picked yet — open the Order view and press “add” on the
          items the centre needs this month. You don't have to order the whole
          gap in one go.
        </Typography>
      ) : (
        <Paper variant="outlined">
          <Table size="small" className={classes.fitTable}>
            <TableHead>
              <TableRow>
                <TableCell width="32%" className={classes.headCell}>
                  Item
                </TableCell>
                <TableCell width="12%" className={classes.headCell}>
                  Order qty
                </TableCell>
                <TableCell width="12%" className={classes.headCell}>
                  Unit cost
                </TableCell>
                <TableCell width="26%" className={classes.headCell}>
                  Remark
                </TableCell>
                <TableCell width="18%" className={classes.headCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <React.Fragment key={g.key}>
                  <TableRow
                    className={classes.orderGroup}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleGroup(g.key)}>
                    <TableCell colSpan={4} className={classes.wrap}>
                      <span className={classes.gameName}>{g.game}</span>
                      <IconButton
                        size="small"
                        className={classes.expandBtn}
                        title="Show this game's items">
                        <ExpandMoreIcon
                          fontSize="small"
                          className={
                            closedGroups.has(g.key)
                              ? undefined
                              : classes.expandOpen
                          }
                        />
                      </IconButton>{' '}
                      <span className={classes.inlineMeta}>
                        {g.programme}
                        {GAME_REF[g.key] ? ` · ${GAME_REF[g.key]}` : ''} ·{' '}
                        {g.rows.length} item
                        {g.rows.length > 1 ? 's' : ''}
                      </span>
                    </TableCell>
                    <TableCell>
                      <CostHint
                        lines={groupCostLines(
                          g.rows.map(({ l }) => ({
                            name: l.item_name,
                            qty: Number(l.qty || 0),
                            unit:
                              l.unit_price != null
                                ? Number(l.unit_price)
                                : null,
                            total:
                              l.unit_price != null
                                ? Number(l.qty || 0) * Number(l.unit_price)
                                : null
                          }))
                        )}>
                        {inr(Math.round(g.total))}
                      </CostHint>
                    </TableCell>
                  </TableRow>
                  {!closedGroups.has(g.key) &&
                    g.rows.map(({ l, idx }) => (
                      <TableRow key={`${l.game}|${l.item_name}`} hover>
                        <TableCell
                          className={classes.wrap}
                          style={{ paddingLeft: 40 }}>
                          {l.item_name}
                          <ItemLink link={l.link} />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            variant="outlined"
                            disabled={locked}
                            value={l.qty}
                            onChange={(e) => {
                              const v = e.target.value
                              onQty(idx, v)
                            }}
                            inputProps={{ min: 0, style: { width: 52 } }}
                          />
                        </TableCell>
                        <TableCell className={classes.muted}>
                          {l.unit_price != null ? (
                            <CostHint
                              lines={unitMathLines(
                                seedSpecFor(l.game, l.item_name),
                                Number(l.unit_price)
                              )}>
                              {inr(l.unit_price)}
                            </CostHint>
                          ) : (
                            'no price yet'
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="remark (optional)"
                            disabled={locked}
                            value={l.remarks || ''}
                            onChange={(e) => {
                              const v = e.target.value
                              onRemark(idx, v)
                            }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          {l.unit_price != null ? (
                            <CostHint
                              lines={[
                                ...unitMathLines(
                                  seedSpecFor(l.game, l.item_name),
                                  Number(l.unit_price)
                                ),
                                `${Number(l.qty || 0)} ordered × ${rupees(
                                  l.unit_price
                                )} = ${inr(
                                  Math.round(
                                    Number(l.qty || 0) * Number(l.unit_price)
                                  )
                                )}`
                              ]}>
                              {inr(
                                Math.round(
                                  Number(l.qty || 0) * Number(l.unit_price)
                                )
                              )}
                            </CostHint>
                          ) : (
                            '— *'
                          )}
                          {!locked && (
                            <Button
                              size="small"
                              style={{ marginLeft: 8 }}
                              onClick={() => onRemove(idx)}>
                              remove
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </React.Fragment>
              ))}

            </TableBody>
          </Table>
        </Paper>
      )}
      {anyUnpriced && lines.length > 0 && (
        <Typography variant="caption" color="textSecondary">
          * some items have no price yet — the total is a floor.
        </Typography>
      )}

      <Dialog open={sopOpen} onClose={() => setSopOpen(false)} maxWidth="xs">
        <DialogTitle>Ordering in parts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="ol" style={{ paddingLeft: 18 }}>
            <li>The Order view shows everything the stock check found short.</li>
            <li>
              Add only what the centre needs this month — the quantity can be
              less than the shortfall.
            </li>
            <li>Submit sends the order to HQ and locks it for the month.</li>
            <li>The rest of the gap stays in the Order view for later months.</li>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSopOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Submit this month's order to HQ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {lines.length} item{lines.length === 1 ? '' : 's'} for{' '}
            {inr(Math.round(total))}
            {anyUnpriced ? ' (plus unpriced items)' : ''}. Submitting locks the
            month — only HQ can reopen it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmOpen(false)}>
            Keep editing
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={saving}
            onClick={() => {
              setConfirmOpen(false)
              onSave(true)
            }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const OrderView = ({ itemCounts, centreName, audit, onAdd, orderKeys }) => {
  const classes = useStyles()
  const [sopOpen, setSopOpen] = React.useState(false)
  // Header filters + frozen header, same pattern as the stock check
  // (user 2026-08-21). Totals and exports follow the filtered rows.
  const [qOrder, setQOrder] = React.useState('')
  const [qCat, setQCat] = React.useState('all')
  // Short-by detail toggle (user 2026-08-22): show required and at-centre
  // before the shortfall, so the number is never a mystery.
  const [showDetail, setShowDetail] = React.useState(false)
  // Same opener/closer as the stock check (user 2026-08-22): game rows
  // collapse their items; closed by default — the header row already says
  // items-short and the subtotal. Multi-open.
  const [openGroups, setOpenGroups] = React.useState(() => new Set())
  const toggleGroup = (key) =>
    setOpenGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const rows = orderRowsFor(itemCounts, audit)
  const q = qOrder.trim().toLowerCase()
  const shownRows = rows.filter(
    (r) =>
      (!q ||
        r.game.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        (r.ref || '').toLowerCase().includes(q)) &&
      (qCat === 'all' || r.programme === qCat)
  )
  // Group the deficits game-wise (user 2026-08-22 P3): one header row per
  // game with its subtotal, items indented beneath — seed order preserved.
  const groups = []
  shownRows.forEach((r) => {
    const key = `${r.programme}|${r.game}`
    let g = groups[groups.length - 1]
    if (!g || g.key !== key) {
      g = {
        key,
        game: r.game,
        programme: r.programme,
        items: [],
        total: 0,
        anyUnpriced: false
      }
      groups.push(g)
    }
    g.items.push(r)
    g.total += r.orderCost || 0
    if (r.orderCost == null) g.anyUnpriced = true
  })
  const anyProvisional = shownRows.some((r) => r.provisional)
  const total = shownRows.reduce((s, r) => s + (r.orderCost || 0), 0)
  const totalNeed = shownRows.reduce((s, r) => s + r.need, 0)
  const anyUnpriced = shownRows.some((r) => r.orderCost == null)


  const sop = (
    <Dialog open={sopOpen} onClose={() => setSopOpen(false)} maxWidth="xs">
      <DialogTitle>How ordering works</DialogTitle>
      <DialogContent>
        <Typography variant="body2" component="ol" style={{ paddingLeft: 18 }}>
          <li>
            Stock check is pre-filled with your July audit counts — open each
            game, correct what has changed, press Save.
          </li>
          <li>Anything saved below its required quantity appears here.</li>
          <li>Add what the centre needs this month to My order, then submit.</li>
          <li>Keep items marked “no price yet” — HQ prices them.</li>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={() => setSopOpen(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )

  if (!rows.length)
    return (
      <div>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="textSecondary">
            Nothing to order — no item is short. Deficits appear here as soon
            as the stock check records one.
          </Typography>
          <IconButton size="small" onClick={() => setSopOpen(true)}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        {sop}
      </div>
    )

  return (
    <div>
      <Box display="flex" alignItems="center" mb={0.5} style={{ gap: 10 }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Game / item / ref — search"
          value={qOrder}
          onChange={(e) => setQOrder(e.target.value)}
          style={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          variant="outlined"
          value={qCat}
          onChange={(e) => setQCat(e.target.value)}
          style={{ minWidth: 170 }}>
          <MenuItem value="all">Category — all</MenuItem>
          {PROGRAMMES.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
        <span style={{ flex: 1 }} />
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          {/* The total was only at the table's foot — surface it where the
              eye lands (user 2026-08-21). Follows the active filter. */}
          <Typography variant="subtitle2">
            Total to order: {inr(Math.round(total))}
            {anyUnpriced ? ' *' : ''}
          </Typography>
          <IconButton
            size="small"
            title="How ordering works"
            onClick={() => setSopOpen(true)}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
        Every item below its required quantity
        {anyProvisional
          ? ' — rows without a saved count use the July audit; review in Stock check before ordering.'
          : ', from the stock check.'}
      </Typography>
      {sop}
      <Paper variant="outlined">
        <Table size="small" className={classes.fitTable}>
          <TableHead>
            <TableRow>
              <TableCell width="46%" className={classes.headCell}>
                Item
              </TableCell>
              <TableCell width="20%" className={classes.headCell}>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  Short by
                  <Chip
                    size="small"
                    clickable
                    label="req · have"
                    title="Also show required and at-centre counts"
                    color={showDetail ? 'primary' : 'default'}
                    onClick={() => setShowDetail((v) => !v)}
                  />
                </Box>
              </TableCell>
              <TableCell width="14%" className={classes.headCell}>
                Unit cost
              </TableCell>
              <TableCell width="20%" className={classes.headCell}>
                To order
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g) => (
              <React.Fragment key={g.key}>
                {/* One header row per game (user 2026-08-22 P3) — the list
                    reads game-wise instead of a wall of line items. */}
                <TableRow
                  className={classes.orderGroup}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleGroup(g.key)}>
                  <TableCell colSpan={3} className={classes.wrap}>
                    <span className={classes.gameName}>{g.game}</span>
                    <IconButton
                      size="small"
                      className={classes.expandBtn}
                      title="Show this game's items">
                      <ExpandMoreIcon
                        fontSize="small"
                        className={
                          openGroups.has(g.key) ? classes.expandOpen : undefined
                        }
                      />
                    </IconButton>{' '}
                    <span className={classes.inlineMeta}>
                      {g.programme}
                      {GAME_REF[g.key] ? ` · ${GAME_REF[g.key]}` : ''} ·{' '}
                      {g.items.length} item
                      {g.items.length > 1 ? 's' : ''} short
                    </span>
                  </TableCell>
                  <TableCell>
                    <CostHint
                      lines={groupCostLines(
                        g.items.map((r) => ({
                          name: r.name,
                          qty: r.need,
                          unit: r.unit,
                          total: r.orderCost
                        }))
                      )}>
                      {inr(Math.round(g.total))}
                      {g.anyUnpriced ? ' *' : ''}
                    </CostHint>
                  </TableCell>
                </TableRow>
                {openGroups.has(g.key) &&
                  g.items.map((r, i) => (
                  <TableRow key={`${r.name}-${i}`} hover>
                    <TableCell
                      className={classes.wrap}
                      style={{ paddingLeft: 40 }}>
                      {r.name}
                      <ItemLink link={r.link} />
                    </TableCell>
                    <TableCell className={classes.short}>
                      {showDetail && (
                        <span className={classes.inlineMeta}>
                          {r.req} req · {r.have} have → order{' '}
                        </span>
                      )}
                      {r.need}
                    </TableCell>
                    <TableCell className={classes.muted}>
                      {r.unit != null ? (
                        <CostHint lines={unitMathLines(r, r.unit)}>
                          {inr(r.unit)}
                        </CostHint>
                      ) : (
                        'no price yet'
                      )}
                    </TableCell>
                    <TableCell>
                      {r.orderCost != null ? (
                        <CostHint
                          lines={[
                            ...unitMathLines(r, r.unit),
                            `${r.need} to order × ${rupees(
                              r.unit
                            )} = ${rupees(r.orderCost)}`
                          ]}>
                          {inr(r.orderCost)}
                        </CostHint>
                      ) : (
                        '— *'
                      )}
                      {/* Order-in-parts: push this deficit onto My order (qty
                          defaults to the shortfall, editable there). */}
                      {onAdd &&
                        (orderKeys.has(`${r.game}|${r.name}`) ? (
                          <span
                            className={classes.ok}
                            style={{ marginLeft: 8, fontSize: 12 }}>
                            added ✓
                          </span>
                        ) : (
                          <Button
                            size="small"
                            color="primary"
                            style={{ marginLeft: 8 }}
                            onClick={() => onAdd(r)}>
                            add
                          </Button>
                        ))}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
            {!shownRows.length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="textSecondary">
                    No short items match the search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow className={classes.totalRow}>
              <TableCell>Total</TableCell>
              <TableCell>{totalNeed}</TableCell>
              <TableCell />
              <TableCell>{inr(total)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
      {anyUnpriced && (
        <Typography variant="caption" color="textSecondary">
          * some short items have no price yet — the total is a floor.
        </Typography>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- games list
// Also rendered by the Settings catalogue switcher as its own catalogue view.
export const GamesList = ({ editable }) => {
  const classes = useStyles()
  useCatalogue()
  const [query, setQuery] = React.useState('')
  const [programme, setProgramme] = React.useState('all')
  // HQ pricing edits (user 2026-08-24 — the sheet is retired): drafts keyed
  // `${uuid}|${field}`, one Save posts them all. Only offered once the
  // backend catalogue is serving — seed rows have no uuids to save against.
  const canEdit = !!editable && CAT_FROM_SERVER
  const [edits, setEdits] = React.useState({})
  const [savingEdits, setSavingEdits] = React.useState(false)
  const editVal = (uuid, field, current) => {
    const k = `${uuid}|${field}`
    if (k in edits) return edits[k]
    return current == null ? '' : String(current)
  }
  const setEdit = (uuid, field, value) =>
    setEdits((prev) => ({ ...prev, [`${uuid}|${field}`]: value }))
  const nEdits = Object.keys(edits).length
  const savePricing = async () => {
    const gamesPayload = {}
    const itemsPayload = {}
    Object.entries(edits).forEach(([k, v]) => {
      const [uuid, field] = k.split('|')
      if (field === 'setCost') {
        gamesPayload[uuid] = { ...(gamesPayload[uuid] || { uuid }), setCost: v }
      } else {
        itemsPayload[uuid] = { ...(itemsPayload[uuid] || { uuid }), [field]: v }
      }
    })
    setSavingEdits(true)
    try {
      await saveAfterschoolCatalogue({
        games: Object.values(gamesPayload),
        items: Object.values(itemsPayload)
      })
      setEdits({})
      ensureCatalogue(true)
      eventAlert(
        'Pricing saved — every centre view now reads it.',
        constants.SUCCESS,
        'Afterschool'
      )
    } catch (e) {
      eventAlert(
        errorText(e, 'Could not save the pricing.'),
        constants.ERROR,
        'Afterschool'
      )
    } finally {
      setSavingEdits(false)
    }
  }
  const editCell = (uuid, field, current, width = 64) => (
    <TextField
      size="small"
      variant="outlined"
      value={editVal(uuid, field, current)}
      disabled={savingEdits}
      onChange={(e) => setEdit(uuid, field, e.target.value)}
      onClick={(e) => e.stopPropagation()}
      inputProps={{ style: { width, textAlign: 'right' } }}
    />
  )
  // View toggle (user 2026-08-22): game-wise (grouped, collapsible — the
  // default) or the flat full list. Searching force-opens the groups so
  // matches are never hidden behind a closed game.
  const [gameWise, setGameWise] = React.useState(true)
  const [openGames, setOpenGames] = React.useState(() => new Set())
  const toggleGame = (key) =>
    setOpenGames((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const q = query.trim().toLowerCase()
  const rows = []
  const groups = []
  GAMES.forEach((g) => {
    if (programme !== 'all' && g.programme !== programme) return
    const items = []
    g.items.forEach((it) => {
      if (
        q &&
        !g.game.toLowerCase().includes(q) &&
        !it.name.toLowerCase().includes(q) &&
        !(it.ref || '').toLowerCase().includes(q)
      )
        return
      items.push({ ...it, game: g.game, programme: g.programme })
      rows.push({ ...it, game: g.game, programme: g.programme })
    })
    if (items.length)
      groups.push({
        key: gk(g),
        game: g.game,
        programme: g.programme,
        uuid: g.uuid,
        setCost: g.setCost,
        items
      })
  })

  return (
    <div>
      <div className={classes.chipRow}>
        <TextField
          className={classes.search}
          size="small"
          variant="outlined"
          label="Search game / item / reference"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Chip
          label="game wise"
          clickable
          color={gameWise ? 'primary' : 'default'}
          onClick={() => setGameWise(true)}
        />
        <Chip
          label="full list"
          clickable
          color={!gameWise ? 'primary' : 'default'}
          onClick={() => setGameWise(false)}
        />
        <Chip
          label="all programmes"
          clickable
          color={programme === 'all' ? 'primary' : 'default'}
          onClick={() => setProgramme('all')}
        />
        {PROGRAMMES.map((p) => (
          <Chip
            key={p}
            label={p}
            clickable
            color={programme === p ? 'primary' : 'default'}
            onClick={() => setProgramme(p)}
          />
        ))}
        {canEdit && (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            size="small"
            disabled={!nEdits || savingEdits}
            onClick={savePricing}>
            {savingEdits
              ? 'saving…'
              : nEdits
              ? `save pricing · ${nEdits}`
              : 'save pricing'}
          </Button>
        )}
      </div>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {rows.length} items
        {canEdit
          ? ' — edit qty, sides, PPP or cost in place; one save applies everywhere. Blank = no price yet.'
          : ''}
      </Typography>
      {gameWise ? (
        <Paper variant="outlined">
          <Table size="small" className={classes.fitTable}>
            <TableHead>
              <TableRow>
                <TableCell
                  width={canEdit ? '32%' : '52%'}
                  className={classes.headCell}>
                  Item
                </TableCell>
                <TableCell
                  width={canEdit ? '16%' : '28%'}
                  className={classes.headCell}>
                  Reference ID
                </TableCell>
                <TableCell
                  width={canEdit ? '10%' : '20%'}
                  align="right"
                  className={classes.headCell}>
                  Qty / centre
                </TableCell>
                {canEdit && (
                  <TableCell width="10%" align="right" className={classes.headCell}>
                    Sides/file
                  </TableCell>
                )}
                {canEdit && (
                  <TableCell width="12%" align="right" className={classes.headCell}>
                    PPP ₹
                  </TableCell>
                )}
                {canEdit && (
                  <TableCell width="20%" align="right" className={classes.headCell}>
                    Cost ₹ (row / set)
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => {
                const open = !!q || openGames.has(g.key)
                return (
                  <React.Fragment key={g.key}>
                    <TableRow
                      className={classes.orderGroup}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleGame(g.key)}>
                      <TableCell className={classes.wrap}>
                        <span className={classes.gameName}>{g.game}</span>
                        <IconButton
                          size="small"
                          className={classes.expandBtn}
                          title="Show this game's items">
                          <ExpandMoreIcon
                            fontSize="small"
                            className={open ? classes.expandOpen : undefined}
                          />
                        </IconButton>{' '}
                        <span className={classes.inlineMeta}>
                          {g.programme} · {g.items.length} item
                          {g.items.length > 1 ? 's' : ''}
                        </span>
                      </TableCell>
                      <TableCell className={classes.muted}>
                        {GAME_REF[g.key] || '—'}
                      </TableCell>
                      <TableCell align="right">1 set</TableCell>
                      {canEdit && <TableCell />}
                      {canEdit && <TableCell />}
                      {canEdit && (
                        <TableCell align="right">
                          {editCell(g.uuid, 'setCost', g.setCost, 72)}
                          <span className={classes.inlineMeta}> set</span>
                        </TableCell>
                      )}
                    </TableRow>
                    {open &&
                      g.items.map((r, i) => (
                        <TableRow key={`${r.ref}-${i}`} hover>
                          <TableCell
                            className={classes.wrap}
                            style={{ paddingLeft: 40 }}>
                            {r.name}
                            <ItemLink link={r.link} />
                          </TableCell>
                          <TableCell className={`${classes.muted} ${classes.wrap}`}>
                            {r.ref || '—'}
                          </TableCell>
                          <TableCell align="right">
                            {canEdit
                              ? editCell(r.uuid, 'qty', r.qty, 44)
                              : r.qty || '—'}
                          </TableCell>
                          {canEdit && (
                            <TableCell align="right">
                              {editCell(r.uuid, 'sides', r.sides, 44)}
                            </TableCell>
                          )}
                          {canEdit && (
                            <TableCell align="right">
                              {editCell(r.uuid, 'ppp', r.ppp, 56)}
                            </TableCell>
                          )}
                          {canEdit && (
                            <TableCell align="right">
                              {editCell(
                                r.uuid,
                                'cost',
                                'cost' in r ? r.cost : null,
                                72
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <Table size="small" className={classes.fitTable}>
            <TableHead>
              <TableRow>
                <TableCell width="14%" className={classes.headCell}>Programme</TableCell>
                <TableCell width="18%" className={classes.headCell}>Game</TableCell>
                <TableCell width="38%" className={classes.headCell}>Item</TableCell>
                <TableCell width="18%" className={classes.headCell}>Reference ID</TableCell>
                <TableCell width="12%" align="right" className={classes.headCell}>
                  Qty / centre
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={`${r.ref}-${i}`} hover>
                  <TableCell className={classes.muted}>{r.programme}</TableCell>
                  <TableCell className={classes.wrap}>{r.game}</TableCell>
                  <TableCell className={classes.wrap}>
                    {r.name}
                    <ItemLink link={r.link} />
                  </TableCell>
                  <TableCell className={`${classes.muted} ${classes.wrap}`}>
                    {r.ref || '—'}
                  </TableCell>
                  <TableCell align="right">{r.qty || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </div>
  )
}

// ------------------------------------------------------------------- wrapper
const AfterschoolGames = ({ centreId, centreName }) => {
  // Swap in the backend catalogue as soon as it arrives (seed until then).
  const catVersion = useCatalogue()
  const classes = useStyles()
  const [tab, setTab] = React.useState('stock')
  const [itemCounts, setItemCounts] = React.useState(() =>
    loadStore(itemStoreKey(centreId))
  )

  // Counts are per centre — reload when the centre changes.
  React.useEffect(() => {
    setItemCounts(loadStore(itemStoreKey(centreId)))
  }, [centreId])

  // Local date, not toISOString() — UTC would stamp yesterday before 05:30 IST.
  const today = () => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`
  }

  // One write for the whole Save — entries: [gameKey, itemKey, have][].
  const handleSaveAll = (entries) => {
    if (!entries.length) return
    const at = today()
    const next = { ...itemCounts }
    entries.forEach(([game, key, have]) => {
      next[game] = { ...(next[game] || {}), [key]: { have, at } }
    })
    setItemCounts(next)
    window.localStorage.setItem(itemStoreKey(centreId), JSON.stringify(next))
  }

  // ------- My order (order-in-parts): server-backed, current month -------
  const orderMonth = today().slice(0, 7)
  const [order, setOrder] = React.useState({
    status: 'draft',
    po_number: '',
    review_remarks: '',
    lines: []
  })
  const [saving, setSaving] = React.useState(false)

  // Server lines carry no link — decorate from the seed by (game, item).
  const linkFor = React.useMemo(() => {
    const m = {}
    GAMES.forEach((g) =>
      g.items.forEach((it) => {
        if (it.link) m[`${g.game}|${it.name}`] = it.link
      })
    )
    return m
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catVersion])

  const loadOrder = React.useCallback(async () => {
    if (!centreId) return
    try {
      const res = await fetchAfterschoolOrder(centreId, orderMonth)
      setOrder({
        status: res.status || 'draft',
        po_number: res.po_number || '',
        review_remarks: res.review_remarks || '',
        lines: (res.lines || []).map((l) => ({
          ...l,
          qty: String(Number(l.qty)),
          unit_price: l.unit_price != null ? Number(l.unit_price) : null,
          link: linkFor[`${l.game}|${l.item_name}`]
        }))
      })
    } catch (e) {
      // Endpoint not deployed yet / network hiccup: the tab shows an empty
      // draft; saving will surface the real error.
    }
  }, [centreId, orderMonth, linkFor])

  React.useEffect(() => {
    loadOrder()
  }, [loadOrder])

  const addToOrder = (r) => {
    if (ORDER_LOCKED.includes(order.status)) return
    setOrder((prev) => ({
      ...prev,
      lines: prev.lines.some((l) => `${l.game}|${l.item_name}` === `${r.game}|${r.name}`)
        ? prev.lines
        : [
            ...prev.lines,
            {
              programme: r.programme,
              game: r.game,
              item_name: r.name,
              reference_id: r.ref || '',
              qty: String(r.need),
              unit_price: r.unit != null ? Math.round(r.unit * 100) / 100 : null,
              remarks: '',
              link: r.link
            }
          ]
    }))
    // Deliberately NO tab jump (user 2026-08-22): centres add several items
    // in a row — the My order tab badge count is the feedback.
  }

  const saveOrder = async (submit) => {
    setSaving(true)
    try {
      const res = await saveAfterschoolOrder({
        centre: centreId,
        month: `${orderMonth}-01`,
        status: submit ? 'submitted' : 'draft',
        lines: order.lines
          .filter((l) => Number(l.qty) > 0)
          .map((l) => ({
            programme: l.programme,
            game: l.game,
            item_name: l.item_name,
            reference_id: l.reference_id || '',
            qty: Number(l.qty),
            unit_price: l.unit_price,
            remarks: l.remarks || ''
          }))
      })
      eventAlert(
        submit
          ? `Submitted to HQ — PO ${res.po_number}`
          : `Draft saved — ${res.lines} item${res.lines === 1 ? '' : 's'}`,
        constants.SUCCESS,
        'Afterschool'
      )
      loadOrder()
    } catch (error) {
      eventAlert(errorText(error, 'Save failed'), constants.ERROR, 'Afterschool')
    } finally {
      setSaving(false)
    }
  }

  // Tab label matches the order view: games short and the ₹ total, from the
  // same saved-or-audit rows — visible without opening the tab.
  const audit = auditFor(centreName)
  const orderRows = orderRowsFor(itemCounts, audit)
  const shortCount = new Set(orderRows.map((r) => `${r.programme}|${r.game}`)).size
  const orderTotal = orderRows.reduce((s, r) => s + (r.orderCost || 0), 0)
  const myOrderTotal = order.lines.reduce(
    (s, l) => s + (l.unit_price != null ? Number(l.qty || 0) * Number(l.unit_price) : 0),
    0
  )

  return (
    <div>
      <Tabs
        className={classes.subTabs}
        value={tab}
        onChange={(e, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary">
        <Tab value="stock" label="Stock check" />
        <Tab
          value="order"
          label={
            shortCount
              ? `Order view · ${shortCount} · ${inr(Math.round(orderTotal))}`
              : 'Order view'
          }
        />
        <Tab
          value="myorder"
          label={
            order.lines.length
              ? `My order · ${order.lines.length} · ${inr(Math.round(myOrderTotal))}`
              : 'My order'
          }
        />
        <Tab value="list" label="Games list" />
      </Tabs>
      {tab === 'stock' && (
        <StockCheck
          itemCounts={itemCounts}
          onSaveAll={handleSaveAll}
          audit={audit}
        />
      )}
      {tab === 'order' && (
        <OrderView
          itemCounts={itemCounts}
          centreName={centreName}
          audit={audit}
          onAdd={ORDER_LOCKED.includes(order.status) ? undefined : addToOrder}
          orderKeys={
            new Set(order.lines.map((l) => `${l.game}|${l.item_name}`))
          }
        />
      )}
      {tab === 'myorder' && (
        <MyOrder
          order={order}
          saving={saving}
          centreName={centreName}
          month={orderMonth}
          onQty={(i, v) =>
            setOrder((prev) => ({
              ...prev,
              lines: prev.lines.map((l, j) => (j === i ? { ...l, qty: v } : l))
            }))
          }
          onRemark={(i, v) =>
            setOrder((prev) => ({
              ...prev,
              lines: prev.lines.map((l, j) =>
                j === i ? { ...l, remarks: v } : l
              )
            }))
          }
          onRemove={(i) =>
            setOrder((prev) => ({
              ...prev,
              lines: prev.lines.filter((l, j) => j !== i)
            }))
          }
          onSave={saveOrder}
        />
      )}
      {tab === 'list' && <GamesList />}
    </div>
  )
}

export default AfterschoolGames
